import { ChainId, Percent, Token } from '@pancakeswap/sdk'
import { mantleTokens, mantleTestnetTokens } from '@pancakeswap/tokens'
import { MAINNET_ADDRESSES, TESTNET_ADDRESSES } from '@pancakeswap/address-config'
import { ChainMap, ChainTokenList } from './types'

export const CHAIN_REFRESH_TIME = {
  // [ChainId.ETHEREUM]: 12_000,
  // [ChainId.GOERLI]: 12_000,
  // [ChainId.MANTLE]: 6_000,
  // [ChainId.MANTLE_TESTNET]: 6_000,
  [ChainId.MANTLE]: 6_000,
  [ChainId.MANTLE_TESTNET]: 6_000,
} as const satisfies Record<ChainId, number>

export const ROUTER_ADDRESS: ChainMap<string> = {
  // [ChainId.ETHEREUM]: '0xEfF92A263d31888d860bD50809A8D171709b7b1c',
  // [ChainId.GOERLI]: '0xEfF92A263d31888d860bD50809A8D171709b7b1c',
  // [ChainId.MANTLE]: '0x10ED43C718714eb63d5aA57B78B54704E256024E',
  // [ChainId.MANTLE_TESTNET]: '0xD99D1c33F9fC3444f8101754aBC46c52416550D1',
  [ChainId.MANTLE]: MAINNET_ADDRESSES.v2.AgniRouter,
  [ChainId.MANTLE_TESTNET]: TESTNET_ADDRESSES.v2.AgniRouter,
}

// used to construct intermediary pairs for trading
export const BASES_TO_CHECK_TRADES_AGAINST: ChainTokenList = {
  [ChainId.MANTLE]: [mantleTokens.wbit, mantleTokens.usdt],
  [ChainId.MANTLE_TESTNET]: [mantleTestnetTokens.wbit, mantleTestnetTokens.mama, mantleTestnetTokens.usdc],
}

/**
 * Additional bases for specific tokens
 * @example { [WBTC.address]: [renBTC], [renBTC.address]: [WBTC] }
 */
export const ADDITIONAL_BASES: { [chainId in ChainId]?: { [tokenAddress: string]: Token[] } } = {}

/**
 * Some tokens can only be swapped via certain pairs, so we override the list of bases that are considered for these
 * tokens.
 * @example [AMPL.address]: [DAI, WNATIVE[ChainId.MANTLE]]
 */
export const CUSTOM_BASES: { [chainId in ChainId]?: { [tokenAddress: string]: Token[] } } = {}

// used for display in the default list when adding liquidity
export const SUGGESTED_BASES: ChainTokenList = {
  // [ChainId.ETHEREUM]: [USDC[ChainId.ETHEREUM], USDT[ChainId.ETHEREUM], WNATIVE[ChainId.ETHEREUM], WBTC_ETH],
  // [ChainId.GOERLI]: [USDC[ChainId.GOERLI], WNATIVE[ChainId.GOERLI], BUSD[ChainId.GOERLI]],
  // [ChainId.MANTLE]: [bscTokens.usdt, mantleTokens.mama, bscTokens.btcb],
  // [ChainId.MANTLE_TESTNET]: [bscTestnetTokens.wbnb, bscTestnetTokens.cake, bscTestnetTokens.busd],
  [ChainId.MANTLE]: [mantleTokens.USDT0,mantleTokens.wbit],
  [ChainId.MANTLE_TESTNET]: [mantleTestnetTokens.wbit, mantleTestnetTokens.mama, mantleTestnetTokens.usdc],
}

// used to construct the list of all pairs we consider by default in the frontend
export const BASES_TO_TRACK_LIQUIDITY_FOR: ChainTokenList = {
  // [ChainId.ETHEREUM]: [USDC[ChainId.ETHEREUM], WNATIVE[ChainId.ETHEREUM], USDT[ChainId.ETHEREUM], WBTC_ETH],
  // [ChainId.GOERLI]: [USDC[ChainId.GOERLI], WNATIVE[ChainId.GOERLI], BUSD[ChainId.GOERLI]],
  // [ChainId.MANTLE]: [mantleTokens.wbit, bscTokens.dai, bscTokens.busd, bscTokens.usdt, mantleTokens.mama],
  // [ChainId.MANTLE_TESTNET]: [bscTestnetTokens.wbnb, bscTestnetTokens.cake, bscTestnetTokens.busd],
  [ChainId.MANTLE]: [mantleTokens.wbit, mantleTokens.usdt],
  [ChainId.MANTLE_TESTNET]: [mantleTestnetTokens.wbit, mantleTestnetTokens.mama, mantleTestnetTokens.usdc],
}

export const PINNED_PAIRS: { readonly [chainId in ChainId]?: [Token, Token][] } = {
  // [ChainId.ETHEREUM]: [
  //   [WNATIVE[ChainId.ETHEREUM], USDC[ChainId.ETHEREUM]],
  //   [WBTC_ETH, WNATIVE[ChainId.ETHEREUM]],
  //   [WNATIVE[ChainId.ETHEREUM], USDT[ChainId.ETHEREUM]],
  // ],
  // [ChainId.MANTLE]: [
  //   [mantleTokens.mama, mantleTokens.wbit],
  //   [bscTokens.busd, bscTokens.usdt],
  //   [bscTokens.dai, bscTokens.usdt],
  // ],

  [ChainId.MANTLE]: [[mantleTokens.wbit, mantleTokens.usdt],[mantleTokens.weth, mantleTokens.cmETH]],
  [ChainId.MANTLE_TESTNET]: [[mantleTestnetTokens.mama, mantleTestnetTokens.usdc]],
}

export const BIG_INT_ZERO = 0n
export const BIG_INT_TEN = 10n

// one basis point
export const BIPS_BASE = 10000n
export const ONE_BIPS = new Percent(1n, BIPS_BASE)
// used for warning states
export const ALLOWED_PRICE_IMPACT_LOW: Percent = new Percent(100n, BIPS_BASE) // 1%
export const ALLOWED_PRICE_IMPACT_MEDIUM: Percent = new Percent(300n, BIPS_BASE) // 3%
export const ALLOWED_PRICE_IMPACT_HIGH: Percent = new Percent(500n, BIPS_BASE) // 5%
// if the price slippage exceeds this number, force the user to type 'confirm' to execute
export const PRICE_IMPACT_WITHOUT_FEE_CONFIRM_MIN: Percent = new Percent(1000n, BIPS_BASE) // 10%
// for non expert mode disable swaps above this
export const BLOCKED_PRICE_IMPACT_NON_EXPERT: Percent = new Percent(1500n, BIPS_BASE) // 15%

// used to ensure the user doesn't send so much BNB so they end up with <.01
export const MIN_BNB: bigint = BIG_INT_TEN ** 16n // .01 BNB
export const BETTER_TRADE_LESS_HOPS_THRESHOLD = new Percent(50n, BIPS_BASE)

export const ZERO_PERCENT = new Percent('0')
export const ONE_HUNDRED_PERCENT = new Percent('1')

export const BASE_FEE = new Percent(25n, BIPS_BASE)
export const INPUT_FRACTION_AFTER_FEE = ONE_HUNDRED_PERCENT.subtract(BASE_FEE)

// BNB
export const DEFAULT_INPUT_CURRENCY = 'MNT'
// CAKE
// export const DEFAULT_OUTPUT_CURRENCY = '0xf6762afb45ac0af7ddc5aa92b885c6ece57874dc'
// export const DEFAULT_OUTPUT_CURRENCY = '0x'

// Handler string is passed to Gelato to use PCS router
export const GELATO_HANDLER = 'pancakeswap'
export const GENERIC_GAS_LIMIT_ORDER_EXECUTION = 500000n

export const LIMIT_ORDERS_DOCS_URL = 'https://docs.pancakeswap.finance/products/pancakeswap-exchange/limit-orders'

export const EXCHANGE_PAGE_PATHS = ['/swap', '/limit-orders', 'liquidity', '/add', '/find', '/remove']
